/**
 * PageGenerationHub — the Settings tab for how pages get WRITTEN: the style specs
 * they're written against and the research settings the builder runs with. Same
 * segmented control as NetworkHub; the active sub-view lives in the URL as
 * `?tab=page-generation&sub=<specs|research>` so it's linkable.
 *
 * Deployment targets used to be here, and before that a top-level nav item. They are
 * now Deliver (`/deliver`): where a page SHIPS is a stage of the work, not a setting,
 * and burying it three clicks inside Settings put publishing further from the pages
 * than the builder's own font size. Settings.tsx redirects the old `?sub=targets`.
 */
import { useSearchParams } from 'react-router-dom';
import { SubTabs } from '../components/common/Tabs';
import PageStyleSpecsSettings from './PageStyleSpecsSettings';
import PageBuilderResearchSettings from './PageBuilderResearchSettings';

export type PageGenerationSub = 'specs' | 'research';
const SUB_KEYS: PageGenerationSub[] = ['specs', 'research'];
const SUB_CONFIG: { key: PageGenerationSub; label: string }[] = [
  { key: 'specs', label: 'Page specs' },
  { key: 'research', label: 'Builder research' },
];

export default function PageGenerationHub({
  initialSub = 'specs',
}: {
  initialSub?: PageGenerationSub;
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get('sub');
  const sub: PageGenerationSub = SUB_KEYS.includes(raw as PageGenerationSub)
    ? (raw as PageGenerationSub)
    : initialSub;

  const setSub = (s: PageGenerationSub) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'page-generation');
    // The DEFAULT SUB IS THE ABSENT SUB, matching the other three hubs. This
    // one always wrote `sub`, so `?tab=page-generation&sub=specs` was canonical
    // here while `?tab=ai` was canonical next door — two spellings of "the
    // default view of a hub", and links copied from one read wrong in the other.
    //
    // Dropping it is safe against the legacy-targets bounce: that fires on
    // `sub=targets` or a leftover site/plan, and a bare `?tab=page-generation`
    // is explicitly NOT treated as legacy (see settingsLinks.ts).
    if (s === 'specs') params.delete('sub');
    else params.set('sub', s);
    // Neither sub-view has a drill-down, so clear any site/plan left over from
    // an old Deployment Targets link.
    params.delete('site');
    params.delete('plan');
    setSearchParams(params, { replace: true });
  };

  return (
    <div>
      <SubTabs
        tabs={SUB_CONFIG}
        active={sub}
        onChange={setSub}
        description="How a page gets written - the shapes it can take, and what the builder is allowed to research."
      />

      {sub === 'specs' && <PageStyleSpecsSettings />}
      {sub === 'research' && <PageBuilderResearchSettings />}
    </div>
  );
}
